import React, { useState, useRef } from 'react';
import { Header, Modal } from '../../../../components';
import { Button, message, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useStateContext } from '../../../../contexts/ContextProvider';
import { useModal } from 'react-modal-hook';
import Table from '../components/Table';
import Form from '../components/Form';
import { useFetchVIPList, useFetchCreateVIP, useFetchUpdateVIP, useFetchDeleteVIP } from "../hooks/useFetch";
import dayjs from 'dayjs';

function VIPMasterData() {
  const { currentColor } = useStateContext();
  const [dataEdit, setDataEdit] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Fetch data with pagination
  const { data: vipData, isLoading, refetch } = useFetchVIPList({ page, pageSize, search });
  const { mutateAsync: createVIP } = useFetchCreateVIP();
  const { mutateAsync: updateVIP } = useFetchUpdateVIP();
  const { mutateAsync: deleteVIP } = useFetchDeleteVIP();

  const titlePage = 'ข้อมูล VIP';
  const formAddRef = useRef(null);
  const formEditRef = useRef(null);

  // Handle pagination change
  const handlePaginationChange = (newPage, newPageSize) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  // Handle search
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when searching
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Format payload before sending to API
  const formatPayload = (values) => {
    return {
      ...values,
      startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : null
    };
  };

  //#region Modal hooks
  const [showAddModal, hideAddModal] = useModal(() => {
    const handleOnOk = async (event) => {
      event.preventDefault();
      message.loading('กรุณารอซักครู่...', 0);
      try {
        const value = await formAddRef.current.validateFields();
        const payload = formatPayload(value);

        await createVIP(payload);
        await message.destroy();
        await message.success('เพิ่มข้อมูลสำเร็จ');
        formAddRef.current?.resetFields();
        await hideAddModal();
        refetch();
      } catch (error) {
        console.error('Error adding data:', error);
        await message.destroy();
        message.error(error?.response?.data?.message || 'ไม่สามารถเพิ่มข้อมูลได้');
      }
    };

    return (
      <Modal
        title={`เพิ่ม ${titlePage}`}
        okText='ยืนยัน'
        cancelText='ยกเลิก'
        width={700}
        onOk={handleOnOk}
        onCancel={() => {
          formAddRef.current?.resetFields();
          hideAddModal();
        }}
      >
        <Form formRef={formAddRef} />
      </Modal>
    );
  }, []);

  const [showEditModal, hideEditModal] = useModal(() => {
    const handleOnOk = async (event) => {
      event.preventDefault();
      message.loading('กรุณารอซักครู่...', 0);
      try {
        const value = await formEditRef.current.validateFields();
        const payload = {
          ...formatPayload(value),
          id: dataEdit.id
        };

        await updateVIP(payload);
        await message.destroy();
        await message.success('แก้ไขข้อมูลสำเร็จ');
        await hideEditModal();
        refetch();
      } catch (error) {
        console.error('Error updating data:', error);
        await message.destroy();
        message.error(error?.response?.data?.message || 'ไม่สามารถแก้ไขข้อมูลได้');
      }
    };

    return (
      <Modal
        title={`แก้ไข ${titlePage}`}
        okText='ยืนยัน'
        cancelText='ยกเลิก'
        width={700}
        onOk={handleOnOk}
        onCancel={() => {
          hideEditModal();
        }}
      >
        <Form formRef={formEditRef} initialValues={dataEdit} />
      </Modal>
    );
  }, [dataEdit]);
  //#endregion

  const handleEdit = (record) => {
    setDataEdit(record);
    showEditModal();
  };

  const handleDelete = async (record) => {
    try {
      message.loading('กำลังลบข้อมูล...', 0);
      await deleteVIP(record.id);
      await message.destroy();
      await message.success('ลบสำเร็จ', 2);
      refetch();
    } catch (error) {
      await message.destroy();
      message.error('ไม่สามารถลบได้');
    }
  };

  return (
    <div className="md:m-10">
      <div className="flex justify-between items-center mb-4">
        <Header category="App" title={titlePage} />
        <Button onClick={showAddModal} className='bg-blue-500 text-white'>
          เพิ่ม VIP
        </Button>
      </div>

      {/* Search Section */}
      <div className="flex gap-2 mb-4">
        <Input
          placeholder="ค้นหา User ID, ชื่อ, ชื่อเล่น..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          allowClear
          onClear={handleSearchClear}
        />
        <Button onClick={handleSearch} type="primary" style={{ backgroundColor: currentColor }}>
          ค้นหา
        </Button>
      </div>

      <Table
        data={vipData?.data || []}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: vipData?.total || 0,
        }}
        onPaginationChange={handlePaginationChange}
      />
    </div>
  );
}

export default VIPMasterData;
